// AuthScreen.tsx — login surface + magic-link landing.
//
// Two responsibilities behind one component:
//
//   1. Phone-input flow (3 states): input → sent → rate-limited.
//      Posts to POST /auth/request-link (always 200 stealth, except 400 on
//      malformed phone or 429 on rate limit). Channel field intentionally
//      omitted — backend picks the user's most-recently-updated channel
//      (Telegram or WhatsApp) per /auth/request-link's "any" fallback path.
//      Hardcoding here was the regression.
//
//   2. Magic-link landing (4 states) when path === '/auth' and ?error= is
//      present. magic.ts redirects here on verify failure with one of
//      ?error=expired | invalid | used (loading is the rare zero-state).
//
// Sent state also surfaces a 6-digit paste-code field for cross-device auth
// The user receives the code on their phone (Telegram bot or WhatsApp),
// types it
// here on their Mac, browser navigates to magic.drishalom.com/<code> →
// cookie set → redirected back to app.
//
// Hebrew copy is locked by design-system §3 + §7 + §8 + §11. Phone field is
// fully LTR (+972 prefix on the LEFT edge per Israeli written form), the
// masked phone in the sent confirmation is dir-isolated inside Hebrew
// text, cooldown counter uses tabular-nums.
//
// AuthScreen is rendered by App.tsx's AuthGate when GET /me returns 401.

import { useEffect, useState } from 'react';
import { getOrgConfig } from '../brand.ts';
import { CodeInput, isCompleteCode } from './CodeInput.tsx';
import { readAnswer, requestLinkOutcome } from '../authProbe.ts';

const RESEND_COOLDOWN_SEC = 60;

// One primary-button look whose disabled state is distinguishable
// WITHOUT relying on opacity. An opacity-only disabled button reads as
// "loading", not "unavailable"; the disabled form changes background, text
// colour and cursor, and the enabled form declares a pointer cursor.
const PRIMARY_BTN =
  'mt-4 w-full cursor-pointer rounded-lg bg-[var(--color-primary)] py-3 text-sm font-bold text-white transition-colors hover:opacity-90 ' +
  'disabled:cursor-not-allowed disabled:bg-[var(--color-border)] disabled:text-[var(--color-muted)] disabled:opacity-100 disabled:hover:opacity-100';

type FlowState =
  | { kind: 'input' }
  // (live-test pass 4): pollNonce drives cross-device login — the
  // SentView polls /auth/poll-login with it; once the user taps the channel's
  // link/button (magic.ts redeems the code), the poll mints the session here.
  | { kind: 'sent'; localDigits: string; cooldownEndsAt: number; pollNonce: string }
  // A 429 from the INPUT screen. It is a send throttle, not an
  // account lockout, so this view still carries the code-entry form: a user
  // holding a valid code from an earlier send must always be able to use it.
  // A 429 while already in `sent` never lands here — see `throttleNotice`.
  | { kind: 'throttled' };

type LandingState = 'loading' | 'expired' | 'invalid' | 'used';

const LANDING_COPY: Record<LandingState, { title: string; body: string }> = {
  loading: { title: 'רגע, מאמתים את הקישור...', body: '' },
  // No 🙏 in an error state. It reads as an apology-plea from the
  // product to the user, which is the wrong register for "here is what
  // happened and what to do next".
  expired: {
    title: 'הקישור פג',
    body: 'קישור תקף 15 דקות בלבד. בקשו אחד חדש.',
  },
  invalid: {
    title: 'הקישור לא תקין',
    body: 'משהו שבור בקישור הזה. בקשו אחד חדש.',
  },
  used: {
    title: 'הקישור כבר נצרך',
    body: 'כל קישור עובד פעם אחת בלבד. בקשו אחד חדש.',
  },
};

// "0501234567" or "501234567" → "050-XXX-4567" (Israeli written form keeps the leading 0)
const maskLocal = (digits: string): string => {
  const stripped = digits.replace(/^0/, '');
  if (stripped.length < 6) return stripped;
  const prefix = stripped.slice(0, 2);
  const last4 = stripped.slice(-4);
  return `0${prefix}-XXX-${last4}`;
};

export const AuthScreen = () => {
  const isMagicLanding =
    typeof window !== 'undefined' &&
    window.location.pathname === '/auth' &&
    new URLSearchParams(window.location.search).has('error');

  return isMagicLanding ? <MagicLinkLanding /> : <PhoneFlow />;
};

const MagicLinkLanding = () => {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('error') ?? '';
  const state: LandingState =
    raw === 'expired' || raw === 'used' ? raw : raw === 'loading' ? 'loading' : 'invalid';
  const copy = LANDING_COPY[state];

  return (
    <Frame>
      <Wordmark />
      <h1 className="text-xl font-bold text-[var(--color-text)]">{copy.title}</h1>
      {copy.body ? (
        <p className="mt-3 text-sm text-[var(--color-muted)]">{copy.body}</p>
      ) : null}
      {state !== 'loading' ? (
        <a
          href="/"
          className="mt-6 inline-flex w-full items-center justify-center rounded-lg bg-[var(--color-primary)] px-6 py-3 text-sm font-bold text-white"
        >
          חזרו לאפליקציה
        </a>
      ) : null}
    </Frame>
  );
};

const PhoneFlow = () => {
  const [state, setState] = useState<FlowState>({ kind: 'input' });
  const [localDigits, setLocalDigits] = useState('');
  // Two independent error slots, so each renders under the
  // control that produced it. One shared slot put a guest/onboarding failure
  // under the phone input, pointing the user at the wrong field.
  const [formError, setFormError] = useState<string | null>(null);
  const [altError, setAltError] = useState<string | null>(null);
  // A throttled RESEND from the sent screen. Surfaced as an
  // inline notice so the SentView (and the code field in it) survives.
  const [throttleNotice, setThrottleNotice] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (digits: string): Promise<void> => {
    if (submitting) return;
    setFormError(null);
    setThrottleNotice(false);
    setSubmitting(true);
    const stripped = digits.replace(/^0/, '');
    try {
      const outcome = requestLinkOutcome(
        await readAnswer(
          await fetch('/auth/request-link', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ phone: `+972${stripped}` }),
          }).catch((e: unknown) => e),
        ),
      );
      if (outcome.kind === 'throttled') {
        // Never swap the screen out from under a user who may be
        // holding a code. From `sent`, stay put and restart the cooldown; from
        // `input`, the throttled view keeps a code-entry form of its own.
        if (state.kind === 'sent') {
          setThrottleNotice(true);
          setState({ ...state, cooldownEndsAt: Date.now() + RESEND_COOLDOWN_SEC * 1000 });
        } else {
          setState({ kind: 'throttled' });
        }
      } else if (outcome.kind === 'invalid') {
        setFormError('מספר לא תקין');
      } else if (outcome.kind === 'error') {
        // Anything but a 200 with a poll nonce is not "sent": keep the input.
        setFormError(outcome.text);
      } else {
        setState({
          kind: 'sent',
          localDigits: digits,
          cooldownEndsAt: Date.now() + RESEND_COOLDOWN_SEC * 1000,
          pollNonce: outcome.pollNonce,
        });
      }
    } catch {
      setFormError('שגיאת רשת');
    } finally {
      setSubmitting(false);
    }
  };

  // S4 4.G2 — start an anonymous guest session for this white-label
  // org. The org-config (resolved at boot) gates the button; on success the
  // guest cookie is set → reload re-probes /me → the App renders GuestChat.
  const startGuest = async (): Promise<void> => {
    setAltError(null);
    try {
      const res = await fetch('/auth/guest-session', {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        window.location.assign('/');
        return;
      }
    } catch {
      /* fall through to the error line */
    }
    setAltError('לא הצלחנו להתחיל כאורח. נסו שוב.');
  };

  // S4 4.W3 — begin web self-onboarding for this org. POST
  // /onboard/start creates a placeholder member + lands a member session on the
  // cookie; the reload re-probes /me (now `onboarding: true`) → the App renders
  // the OnboardingChat shell. Gated by the org-config self_onboard_enabled flag.
  const startOnboard = async (): Promise<void> => {
    setAltError(null);
    try {
      const res = await fetch('/onboard/start', {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        window.location.assign('/');
        return;
      }
    } catch {
      /* fall through to the error line */
    }
    setAltError('לא הצלחנו להתחיל. נסו שוב.');
  };

  // Throttled from the input screen: state the throttle, and keep
  // a code-entry form so a held code is still usable.
  if (state.kind === 'throttled') {
    return (
      <Frame>
        <Wordmark />
        <h1 className="text-xl font-bold text-[var(--color-text)]">רגע, יותר מדי בקשות</h1>
        {/* S2 — names neither the phone number nor a duration. The
            backend runs four limiter arms with different windows (the per-IP
            arm is 10 per 10 minutes, not an hour), and naming the number
            leaked which number was tried. */}
        <p className="mt-3 text-sm text-[var(--color-muted)]">
          בקשת קודים רבות מדי בזמן קצר. המתינו מעט ונסו שוב.
        </p>
        <p className="mt-4 text-xs text-[var(--color-muted)]">
          כבר קיבלתם קוד? אפשר להזין אותו כאן.
        </p>
        <CodeEntryForm />
        <button
          type="button"
          onClick={() => {
            setState({ kind: 'input' });
            setFormError(null);
          }}
          className="mt-6 cursor-pointer text-xs text-[var(--color-primary)] underline"
        >
          חזרה למסך הכניסה
        </button>
      </Frame>
    );
  }

  if (state.kind === 'sent') {
    return (
      <SentView
        localDigits={state.localDigits}
        cooldownEndsAt={state.cooldownEndsAt}
        pollNonce={state.pollNonce}
        throttled={throttleNotice}
        // Resend re-submits — submit() itself re-sets state with the fresh
        // poll_nonce, so we do not setState here (that would race a stale nonce).
        onResend={() => void submit(state.localDigits)}
        onChangeNumber={() => {
          setState({ kind: 'input' });
          setFormError(null);
          setThrottleNotice(false);
        }}
      />
    );
  }

  return (
    <Frame>
      <Wordmark />
      <h1 className="text-xl font-bold text-[var(--color-text)]">כניסה</h1>
      <p className="mt-3 text-sm text-[var(--color-muted)]">
        נשלח לכם קוד לערוץ שבו אתם רשומים (טלגרם או ווטסאפ)
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit(localDigits);
        }}
        className="mt-6 w-full"
      >
        <div
          dir="ltr"
          className="flex items-stretch rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] focus-within:border-[var(--color-primary)]"
        >
          <span
            className="flex items-center border-l border-[var(--color-border)] px-3 text-sm text-[var(--color-muted)]"
            aria-hidden="true"
          >
            +972
          </span>
          <input
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            value={localDigits}
            onChange={(e) => setLocalDigits(e.target.value.replace(/[^\d]/g, ''))}
            placeholder="050-000-0000"
            maxLength={10}
            aria-label="מספר טלפון"
            className="flex-1 bg-transparent px-3 py-3 text-sm outline-none"
            autoFocus
          />
        </div>
        {/* S3 — the phone form's own error slot. Guest / onboarding
            failures render under their own buttons below, not here. */}
        {formError ? (
          <p className="mt-2 text-xs text-[var(--color-bad)]" role="alert">
            {formError}
          </p>
        ) : null}
        <button
          type="submit"
          disabled={localDigits.length < 9 || submitting}
          className={PRIMARY_BTN}
        >
          {submitting ? 'שליחה...' : 'שלחו לי את הקוד'}
        </button>
      </form>
      {getOrgConfig().self_onboard_enabled || getOrgConfig().guest_enabled ? (
        <div className="mt-6 w-full">
          <div className="mb-4 flex items-center gap-3 text-xs text-[var(--color-muted)]">
            <span className="h-px flex-1 bg-[var(--color-border)]" />
            <span>או</span>
            <span className="h-px flex-1 bg-[var(--color-border)]" />
          </div>
          {/* S4 4.W3 — self-onboarding entry (primary CTA when offered). */}
          {getOrgConfig().self_onboard_enabled ? (
            <button
              type="button"
              onClick={() => void startOnboard()}
              className="w-full cursor-pointer rounded-lg bg-[var(--color-primary)] py-3 text-sm font-bold text-white transition-colors hover:opacity-90"
            >
              אני חדש
            </button>
          ) : null}
          {/* S4 4.G2 — read-only guest browse entry. */}
          {getOrgConfig().guest_enabled ? (
            <button
              type="button"
              onClick={() => void startGuest()}
              className={`w-full cursor-pointer rounded-lg border border-[var(--color-primary)] py-3 text-sm font-bold text-[var(--color-primary)] transition-colors hover:bg-[var(--color-primary-light)] ${
                getOrgConfig().self_onboard_enabled ? 'mt-3' : ''
              }`}
            >
              כניסה כאורחים
            </button>
          ) : null}
          {/* S3 — guest / onboarding failures render under the buttons
              that produced them, not under the phone input. */}
          {altError ? (
            <p className="mt-2 text-xs text-[var(--color-bad)]" role="alert">
              {altError}
            </p>
          ) : null}
        </div>
      ) : null}
    </Frame>
  );
};

// The 8-digit code entry, extracted so BOTH the sent screen and
// the throttled screen carry it. A send throttle must never remove the only
// affordance for a code the user already holds; keeping one component is what
// makes that true in both places rather than in one of them.
const CodeEntryForm = () => {
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitCode = async (): Promise<void> => {
    if (!isCompleteCode(code) || verifying) return;
    setVerifying(true);
    setError(null);
    try {
      const res = await fetch('/auth/verify-code', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ code }),
        credentials: 'include',
      });
      if (res.ok) {
        window.location.assign('/');
        return;
      }
      setError('הקוד לא תקין או שפג תוקפו');
    } catch {
      setError('שגיאת רשת');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submitCode();
      }}
      className="mt-6 w-full"
    >
      <CodeInput value={code} onChange={setCode} onSubmit={() => void submitCode()} />
      {error ? (
        <p className="mt-2 text-xs text-[var(--color-bad)]" role="alert">
          {error}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={!isCompleteCode(code) || verifying}
        className={PRIMARY_BTN}
      >
        {verifying ? 'התחברות...' : 'התחברו'}
      </button>
    </form>
  );
};

const SentView = ({
  localDigits,
  cooldownEndsAt,
  pollNonce,
  throttled,
  onResend,
  onChangeNumber,
}: {
  localDigits: string;
  cooldownEndsAt: number;
  pollNonce: string;
  throttled: boolean;
  onResend: () => void;
  onChangeNumber: () => void;
}) => {
  const [secondsLeft, setSecondsLeft] = useState(() =>
    Math.max(0, Math.ceil((cooldownEndsAt - Date.now()) / 1000)),
  );

  useEffect(() => {
    const tick = () =>
      setSecondsLeft(Math.max(0, Math.ceil((cooldownEndsAt - Date.now()) / 1000)));
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [cooldownEndsAt]);

  // (live-test pass 4): cross-device login poll. While this screen is
  // open, poll /auth/poll-login every 2s. The moment the user taps the
  // channel's link/button on their phone (magic.ts redeems the code), the poll
  // mints the session here + sets the cookie → reload into the app.
  useEffect(() => {
    if (!pollNonce) return;
    let stopped = false;
    const id = window.setInterval(() => {
      void fetch('/auth/poll-login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ poll_nonce: pollNonce }),
        credentials: 'include',
      })
        .then(async (res) => {
          if (stopped || !res.ok) return;
          const body = (await res.json().catch(() => ({}))) as { ok?: boolean };
          if (body.ok) {
            stopped = true;
            window.location.assign('/');
          }
        })
        .catch(() => {
          /* transient — keep polling */
        });
    }, 2000);
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [pollNonce]);

  const masked = maskLocal(localDigits);

  return (
    <Frame>
      <Wordmark />
      <h1 className="text-xl font-bold text-[var(--color-text)]">קוד נשלח</h1>
      <p className="mt-3 text-sm text-[var(--color-muted)]">
        שלחנו לכם קוד ל-
        <span dir="ltr" style={{ unicodeBidi: 'isolate' }} className="font-bold">
          {masked}
        </span>
        {' '}בערוץ שבו אתם רשומים (טלגרם או ווטסאפ)
      </p>
      <p className="mt-2 text-xs text-[var(--color-muted)]">
        פתחו את ההודעה ולחצו על הקישור, או הזינו את הקוד פה — נמשיך אוטומטית.
      </p>
      {/* Code-paste path — POST the typed code, mint the session in place, no
          cross-subdomain navigation. Same shared magic-code verify as the
          channel's link/button. */}
      <CodeEntryForm />
      <div className="mt-6 w-full border-t border-[var(--color-border)] pt-4">
        {/* S1 — a throttled RESEND. The screen (and the code field
            above) stays put; only the resend affordance is held back.
            A 429 says nothing about the held code — it may have expired,
            been used or been replaced — so the line claims no validity. */}
        {throttled ? (
          <p className="mb-3 text-xs text-[var(--color-muted)]" role="status">
            שליחת קוד חדש מוגבלת כרגע. קוד שכבר קיבלתם אפשר להזין כאן.
          </p>
        ) : null}
        {secondsLeft > 0 ? (
          <button
            type="button"
            disabled
            aria-live="polite"
            className="w-full cursor-not-allowed rounded-lg bg-[var(--color-primary-light)] py-3 text-xs text-[var(--color-muted)] tabular-nums"
          >
            קוד חדש בעוד {secondsLeft} שניות
          </button>
        ) : (
          <button
            type="button"
            onClick={onResend}
            className="w-full cursor-pointer rounded-lg bg-[var(--color-primary-light)] py-3 text-xs text-[var(--color-primary)] transition-colors hover:brightness-95"
          >
            שלחו קוד חדש
          </button>
        )}
        <button
          type="button"
          onClick={onChangeNumber}
          className="mt-3 cursor-pointer text-xs text-[var(--color-primary)] underline"
        >
          החליפו מספר
        </button>
      </div>
    </Frame>
  );
};

const Frame = ({ children }: { children: React.ReactNode }) => (
  <div className="flex h-full items-center justify-center px-6 py-8">
    <div className="flex w-full max-w-sm flex-col items-center text-center">
      {children}
    </div>
  </div>
);

// S4 4.W2 — brand-aware: a white-label org shows its own name on the
// login/landing surface; the default app keeps "דרישלום".
const Wordmark = () => {
  const cfg = getOrgConfig();
  const name = cfg.brand?.name ?? cfg.name ?? 'דרישלום';
  return (
    <div className="mb-8 text-3xl font-bold text-[var(--color-primary)]">
      {name}
    </div>
  );
};
